import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { Scale, FileText, CalendarCheck, MessageSquare, ShieldCheck, BookOpen, Phone, Sparkles } from "lucide-react";

export default function LawFirms() {
  return (
    <IndustryPage
      slug="law-firms"
      industry="Law Firms"
      Icon={Scale}
      accent="#0F766E"
      seoTitle="AI Intake Assistant for Law Firms, Solicitors & Legal Practices"
      seoDescription="HelloSir.ai is the AI intake assistant for law firms, solicitors and legal practices. Captures case enquiries, qualifies clients, schedules consultations and answers procedural questions 24/7 — multilingual on chat, web voice and phone. Never gives legal advice."
      seoKeywords={[
        "AI for law firms", "AI legal intake", "AI for solicitors",
        "AI receptionist law firm", "AI client intake legal",
        "AI for personal injury law firm", "AI for immigration lawyers",
        "AI for family law", "AI for conveyancing", "legal chatbot UK",
      ]}
      hero={{
        headline: "Capture every case — even the ones that call at midnight.",
        sub: "HelloSir.ai is your firm's after-hours intake clerk. It listens, qualifies, schedules a consultation and pushes the lead into your case-management system — without ever offering legal advice.",
      }}
      painPoints={[
        { Icon: Phone,    title: "Cases miss the firm by minutes", desc: "Personal injury, immigration and family-law leads call competitors when nobody answers." },
        { Icon: FileText, title: "Intake takes forever",            desc: "Long forms and back-and-forth emails kill conversion." },
        { Icon: BookOpen, title: "Same procedural questions",       desc: "Costs, timelines, documents required — the same questions every day." },
      ]}
      capabilities={[
        { Icon: FileText,     title: "Client intake",          desc: "Captures matter type, party names, key dates, jurisdiction — straight into your CMS." },
        { Icon: ShieldCheck,  title: "Conflict screen",         desc: "Cross-checks new enquiries against your conflict database before booking a consult." },
        { Icon: CalendarCheck, title: "Books consultations",    desc: "Live sync with each fee-earner's calendar — paid or free as you configure." },
        { Icon: MessageSquare, title: "Procedural Q&A",         desc: "Explains *how* a process works — never *what* a client should do (no legal advice)." },
        { Icon: BookOpen,     title: "Multi-practice",           desc: "Templates for personal injury, immigration, family, conveyancing, employment, and corporate." },
        { Icon: Sparkles,     title: "Trust-account aware",      desc: "Never quotes pricing as a binding fee — directs to formal client-care letters and engagement terms." },
      ]}
      sampleChats={[
        { q: "I was rear-ended in London last week, neck pain. Can you help?",
          a: "I'm sorry to hear that. We handle road-traffic personal injury. To check we can take this case, I'll need: ① date and location of the collision, ② whether you've seen a GP, ③ insurance details. Shall we book a free 15-min call this week?" },
        { q: "How long does a UK divorce take?",
          a: "Procedurally, an uncontested divorce in England & Wales typically takes 26–32 weeks under the no-fault regime. Children and finances often run separately. I can book a paid 30-min initial consult with one of our family solicitors — would Tuesday at 4pm work?" },
        { q: "What's the cost?",
          a: "Initial consults are £150 + VAT. Fixed-fee and hourly options for the matter itself are explained in our client-care letter sent after the consult. Shall I book?" },
      ]}
      sources={[
        "Your firm's procedural FAQs",
        "Practice-area pages (PI, immigration, family, etc.)",
        "Fee-earners' calendars",
        "Conflict database (via API)",
      ]}
      integrations={[
        "Clio", "LEAP", "Actionstep", "PracticePanther", "MyCase",
        "Office 365", "Google Workspace", "DocuSign", "Stripe (consult payments)", "Zapier",
      ]}
      metrics={[
        { value: "+44%", label: "After-hours enquiries captured" },
        { value: "<3 min", label: "Avg time from enquiry → booked consult" },
        { value: "24/7", label: "Multilingual coverage" },
        { value: "100%", label: "Cases logged in CMS" },
      ]}
      faqs={[
        { q: "Will the AI give legal advice?",
          a: "Never. The agent is guard-railed to explain *procedure* only and to route any \"what should I do\" question to a qualified solicitor." },
        { q: "How does conflict-of-interest screening work?",
          a: "We hit your conflict register API at the end of intake. If a match is detected the agent politely declines and offers a referral list." },
        { q: "Is it OK under SRA / professional rules?",
          a: "Yes when configured correctly. We provide a regulator-friendly script template that includes mandatory disclosures and avoids any unauthorised practice." },
        { q: "Can it integrate with our case-management system?",
          a: "Direct integrations with Clio, LEAP, Actionstep, PracticePanther and MyCase. Anything else via webhook or Zapier." },
      ]}
    />
  );
}
